import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ReferralSettings, UpdateReferralSettingsRequest } from "../../../types/referral";

interface ReferralSettingsFormProps {
  settings: ReferralSettings;
  onSave: (data: UpdateReferralSettingsRequest) => void;
  isSaving: boolean;
}

type FormValues = {
  isEnabled: boolean;
  discountValue: number;
  discountType: number;
  feeType: number;
  maxReferralsPerUser: number;
  rewardExpirationDays: number;
  minOrderValue: string;
  routeRestrictions: string;
  promotionalText: string;
  refereeFirstOrderDiscountEnabled: boolean;
  refereeFirstOrderDiscountValue: number;
  refereeFirstOrderDiscountType: number;
};

const discountTypeToNumber = (type: string): number => {
  if (type === "Percentage") return 1;
  if (type === "FixedAmount") return 2;
  return 1;
};

const feeTypeToNumber = (type: string): number => {
  switch (type) {
    case "ServiceFee": return 1;
    case "CargoFee": return 2;
    case "AllFees": return 3;
    case "Tax": return 4;
    case "TaxAndFees": return 5;
    default: return 1;
  }
};

const ReferralSettingsForm: React.FC<ReferralSettingsFormProps> = ({
  settings,
  onSave,
  isSaving,
}) => {
  const { t } = useTranslation();

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      isEnabled: settings.isEnabled,
      discountValue: settings.discountValue,
      discountType: discountTypeToNumber(settings.discountType),
      feeType: feeTypeToNumber(settings.feeType),
      maxReferralsPerUser: settings.maxReferralsPerUser,
      rewardExpirationDays: settings.rewardExpirationDays,
      minOrderValue: settings.minOrderValue != null ? String(settings.minOrderValue) : "",
      routeRestrictions: settings.routeRestrictions ?? "",
      promotionalText: settings.promotionalText ?? "",
      refereeFirstOrderDiscountEnabled: settings.refereeFirstOrderDiscountEnabled,
      refereeFirstOrderDiscountValue: settings.refereeFirstOrderDiscountValue,
      refereeFirstOrderDiscountType: discountTypeToNumber(settings.refereeFirstOrderDiscountType),
    },
  });

  useEffect(() => {
    reset({
      isEnabled: settings.isEnabled,
      discountValue: settings.discountValue,
      discountType: discountTypeToNumber(settings.discountType),
      feeType: feeTypeToNumber(settings.feeType),
      maxReferralsPerUser: settings.maxReferralsPerUser,
      rewardExpirationDays: settings.rewardExpirationDays,
      minOrderValue: settings.minOrderValue != null ? String(settings.minOrderValue) : "",
      routeRestrictions: settings.routeRestrictions ?? "",
      promotionalText: settings.promotionalText ?? "",
      refereeFirstOrderDiscountEnabled: settings.refereeFirstOrderDiscountEnabled,
      refereeFirstOrderDiscountValue: settings.refereeFirstOrderDiscountValue,
      refereeFirstOrderDiscountType: discountTypeToNumber(settings.refereeFirstOrderDiscountType),
    });
  }, [settings, reset]);

  const onSubmit = (values: FormValues) => {
    const payload: UpdateReferralSettingsRequest = {
      isEnabled: values.isEnabled,
      discountValue: Number(values.discountValue),
      discountType: values.discountType,
      feeType: values.feeType,
      maxReferralsPerUser: Number(values.maxReferralsPerUser),
      rewardExpirationDays: Number(values.rewardExpirationDays),
      minOrderValue: values.minOrderValue !== "" ? Number(values.minOrderValue) : undefined,
      routeRestrictions: values.routeRestrictions || undefined,
      promotionalText: values.promotionalText || undefined,
      refereeFirstOrderDiscountEnabled: values.refereeFirstOrderDiscountEnabled,
      refereeFirstOrderDiscountValue: Number(values.refereeFirstOrderDiscountValue),
      refereeFirstOrderDiscountType: values.refereeFirstOrderDiscountType,
    };
    onSave(payload);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {t("admin.referral.settingsTitle")}
      </Typography>

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Grid container spacing={3}>
          {/* Enable / disable */}
          <Grid item xs={12}>
            <Controller
              name="isEnabled"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label={t("admin.referral.enableReferralProgram")}
                />
              )}
            />
          </Grid>

          {/* Discount Value */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="discountValue"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("admin.referral.discountValue")}
                  type="number"
                  inputProps={{ min: 0, step: "any" }}
                  fullWidth
                  required
                />
              )}
            />
          </Grid>

          {/* Discount Type */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="discountType"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth required>
                  <InputLabel>{t("admin.referral.discountType")}</InputLabel>
                  <Select {...field} label={t("admin.referral.discountType")}>
                    <MenuItem value={1}>{t("admin.referral.discountTypePercentage")}</MenuItem>
                    <MenuItem value={2}>{t("admin.referral.discountTypeFixed")}</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          {/* Fee Type */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="feeType"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth required>
                  <InputLabel>{t("admin.referral.feeType")}</InputLabel>
                  <Select {...field} label={t("admin.referral.feeType")}>
                    <MenuItem value={1}>{t("admin.referral.feeTypeServiceFee")}</MenuItem>
                    <MenuItem value={2}>{t("admin.referral.feeTypeCargoFee")}</MenuItem>
                    <MenuItem value={3}>{t("admin.referral.feeTypeAllFees")}</MenuItem>
                    <MenuItem value={4}>{t("admin.referral.feeTypeTax")}</MenuItem>
                    <MenuItem value={5}>{t("admin.referral.feeTypeTaxAndFees")}</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          {/* Max Referrals Per User */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="maxReferralsPerUser"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("admin.referral.maxReferralsPerUser")}
                  type="number"
                  inputProps={{ min: 1 }}
                  fullWidth
                  required
                />
              )}
            />
          </Grid>

          {/* Reward Expiration Days */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="rewardExpirationDays"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("admin.referral.rewardExpirationDays")}
                  type="number"
                  inputProps={{ min: 1 }}
                  fullWidth
                  required
                />
              )}
            />
          </Grid>

          {/* Min Order Value */}
          <Grid item xs={12} sm={6}>
            <Controller
              name="minOrderValue"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("admin.referral.minOrderValue")}
                  type="number"
                  inputProps={{ min: 0, step: "any" }}
                  fullWidth
                  helperText={t("admin.referral.optional")}
                />
              )}
            />
          </Grid>

          {/* Route Restrictions */}
          <Grid item xs={12}>
            <Controller
              name="routeRestrictions"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("admin.referral.routeRestrictions")}
                  fullWidth
                  helperText={t("admin.referral.optional")}
                />
              )}
            />
          </Grid>

          {/* Promotional Text */}
          <Grid item xs={12}>
            <Controller
              name="promotionalText"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("admin.referral.promotionalText")}
                  multiline
                  rows={3}
                  fullWidth
                  helperText={t("admin.referral.optional")}
                />
              )}
            />
          </Grid>

          {/* Referee First Order Discount Section */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
              {t("admin.referral.refereeFirstOrderDiscount")}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="refereeFirstOrderDiscountEnabled"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label={t("admin.referral.refereeFirstOrderDiscountEnabled")}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="refereeFirstOrderDiscountValue"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t("admin.referral.refereeFirstOrderDiscountValue")}
                  type="number"
                  inputProps={{ min: 0, step: "any" }}
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Controller
              name="refereeFirstOrderDiscountType"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>{t("admin.referral.refereeFirstOrderDiscountType")}</InputLabel>
                  <Select {...field} label={t("admin.referral.refereeFirstOrderDiscountType")}>
                    <MenuItem value={1}>{t("admin.referral.discountTypePercentage")}</MenuItem>
                    <MenuItem value={2}>{t("admin.referral.discountTypeFixed")}</MenuItem>
                  </Select>
                </FormControl>
              )}
            />
          </Grid>

          {/* Save Button */}
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={16} /> : undefined}
            >
              {isSaving ? t("admin.referral.saving") : t("admin.referral.save")}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default ReferralSettingsForm;
